'use client';

import { Button as HeroButton, Spinner } from '@heroui/react';
import { ReactNode } from 'react';

export interface ButtonProps {
  children?: ReactNode;
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'outline'
    | 'ghost'
    | 'danger'
    | 'danger-soft';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  isPending?: boolean;
  isDisabled?: boolean;
  isIconOnly?: boolean;
  fullWidth?: boolean;
  onPress?: () => void;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  startContent?: ReactNode;
  endContent?: ReactNode;
  'aria-label'?: string;
  align?: 'start' | 'end';
  as?: any;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
}

export const Button = ({
  children,
  align,
  variant = 'primary',
  size = 'md',
  isLoading,
  isPending,
  isDisabled,
  isIconOnly,
  fullWidth,
  onPress,
  type = 'button',
  form,
  startContent,
  endContent,
  'aria-label': ariaLabel,
  as,
  href,
  target,
  rel,
  className = '',
}: ButtonProps) => {
  const pendingState = isPending || isLoading;
  const withAvatarClassName =
    (startContent as any)?.type?.name === 'Avatar' ? 'pl-[2px]' : '';
  const withAlignClassName = align ? `justify-${align}` : 'justify-center';
  const Component = HeroButton as any;

  return (
    <Component
      as={as}
      href={href}
      target={target}
      rel={rel}
      className={`inline-flex items-center gap-2 cursor-pointer ${fullWidth ? 'w-full grow-1' : ''} ${withAvatarClassName} ${withAlignClassName} ${className}`.trim()}
      variant={variant}
      size={size}
      isPending={pendingState}
      isDisabled={isDisabled}
      isIconOnly={isIconOnly}
      fullWidth={fullWidth}
      onPress={onPress}
      type={type}
      form={form}
      aria-label={ariaLabel}
    >
      {({ isPending: internalPending }: { isPending?: boolean }) => (
        <>
          {internalPending ? (
            <Spinner color="current" size="sm" />
          ) : (
            startContent
          )}
          {children && <span>{children}</span>}
          {endContent}
        </>
      )}
    </Component>
  );
};
